import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
	Card,
	Crumbs,
	DASH,
	ErrorLine,
	Loading,
	MonthBars,
	Tile,
	full,
	groupOf,
	href,
	linkCls,
	monthLabel,
	nowYm,
	short,
	snCls,
	sumOrNull,
	useMatrix,
} from './shared';

/* ── Site: one site across the year ─────────────────────────────────────────── */

export const SiteView: React.FC<{ fy: string; site: string }> = ({ fy, site }) => {
	const { loading, error, months, cell, sites } = useMatrix(fy);
	const rows = months.map(m => ({ m, c: cell(site, m) }));
	const have = rows.filter(r => r.c);
	const net = sumOrNull(have.map(r => r.c!.net_pay));
	const latest = have[have.length - 1];
	const employer = sumOrNull(have.flatMap(r => [r.c!.pf_er, r.c!.esi_er]));

	return (
		<>
			<Crumbs items={[{ label: 'Payroll', to: href({ fy }) }, { label: site }]} />
			<div className='flex items-end justify-between mb-5'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900'>{site}</h1>
					<p className='text-sm text-gray-500'>
						{groupOf(site)} · FY {fy} · click a month to see each person
					</p>
				</div>
				{sites.length > 0 && (
					<select
						value={site}
						onChange={e => (window.location.href = href({ fy, site: e.target.value }))}
						className='border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white'
						aria-label='Switch site'
					>
						{!sites.includes(site) && <option value={site}>{site}</option>}
						{sites.map(s => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				)}
			</div>
			{loading && <Loading />}
			{error && <ErrorLine msg={error} />}
			{!loading && (
				<>
					<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
						<Tile
							label='Net pay, FY so far'
							value={short(net)}
							sub={`${have.length} of ${months.length} months have a sheet`}
						/>
						<Tile
							label='Average per month'
							value={short(net === null ? null : net / have.length)}
							sub='months with a sheet only'
						/>
						<Tile
							label={latest ? `People · ${monthLabel(latest.m)}` : 'People'}
							value={latest ? String(latest.c!.people) : DASH}
							to={latest ? href({ fy, site, month: latest.m }) : undefined}
							sub={latest ? 'open the latest sheet' : undefined}
						/>
						<Tile
							label='Employer PF + ESIC, FY'
							value={short(employer)}
							sub={employer === null ? 'not in these sheets' : 'from the sheets'}
						/>
					</div>
					<Card title='Net pay by wage month' className='mb-4'>
						<MonthBars
							items={rows.map(r => ({
								month: r.m,
								value: r.c?.net_pay ?? null,
								sub: r.c ? `${r.c.people} people` : r.m >= nowYm ? 'not yet' : 'No sheet',
								inProgress: r.m >= nowYm,
								to: r.c ? href({ fy, site, month: r.m }) : undefined,
							}))}
						/>
					</Card>
					<Card>
						<table className='w-full text-[13px]'>
							<thead className='text-gray-500 border-b border-gray-100'>
								<tr>
									<th className={snCls}>#</th>
									{[
										'Wage month',
										'People',
										'Gross',
										'Net pay',
										'PF employee',
										'PF employer',
										'ESIC employee',
										'ESIC employer',
										'Final sheet',
									].map((h, i) => (
										<th
											key={h}
											className={`px-3 py-2 font-medium ${i === 0 || i === 8 ? 'text-left' : 'text-right'}`}
										>
											{h}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.map(({ m, c }, i) =>
									c ? (
										<tr key={m} className='border-t border-gray-100 hover:bg-primary-50/50'>
											<td className={snCls}>{i + 1}</td>
											<td className='px-3 py-2'>
												<Link
													to={href({ fy, site, month: m })}
													className={`${linkCls} font-medium inline-flex items-center gap-1`}
												>
													{monthLabel(m)} <ChevronRight className='w-3.5 h-3.5' />
												</Link>
											</td>
											<td className='px-3 py-2 text-right'>{c.people}</td>
											<td
												className={`px-3 py-2 text-right ${c.gross === null ? 'text-gray-300' : ''}`}
											>
												{full(c.gross)}
											</td>
											<td className='px-3 py-2 text-right font-semibold'>{full(c.net_pay)}</td>
											<td
												className={`px-3 py-2 text-right ${c.pf_ee === null ? 'text-gray-300' : ''}`}
											>
												{full(c.pf_ee)}
											</td>
											<td
												className={`px-3 py-2 text-right ${c.pf_er === null ? 'text-gray-300' : ''}`}
											>
												{full(c.pf_er)}
											</td>
											<td
												className={`px-3 py-2 text-right ${c.esi_ee === null ? 'text-gray-300' : ''}`}
											>
												{full(c.esi_ee)}
											</td>
											<td
												className={`px-3 py-2 text-right ${c.esi_er === null ? 'text-gray-300' : ''}`}
											>
												{full(c.esi_er)}
											</td>
											<td
												className='px-3 py-2 text-gray-500 truncate max-w-[220px]'
												title={`${c.file_name} · ${c.tab_name}`}
											>
												{c.file_name}
												{c.versions_seen > 1 && (
													<span className='ml-1 text-[11px]'>({c.versions_seen} versions)</span>
												)}
											</td>
										</tr>
									) : (
										<tr key={m} className='border-t border-gray-100 text-gray-300'>
											<td className={snCls}>{i + 1}</td>
											<td className='px-3 py-2'>{monthLabel(m)}</td>
											<td className='px-3 py-2' colSpan={8}>
												No sheet
											</td>
										</tr>
									)
								)}
							</tbody>
							<tfoot className='border-t-2 border-gray-200 font-semibold'>
								<tr>
									<td />
									<td className='px-3 py-2'>FY total</td>
									<td className='px-3 py-2 text-right'>
										{have.reduce((a, r) => a + r.c!.people, 0).toLocaleString('en-IN')}
									</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(have.map(r => r.c!.gross)))}
									</td>
									<td className='px-3 py-2 text-right'>{full(net)}</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(have.map(r => r.c!.pf_ee)))}
									</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(have.map(r => r.c!.pf_er)))}
									</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(have.map(r => r.c!.esi_ee)))}
									</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(have.map(r => r.c!.esi_er)))}
									</td>
									<td />
								</tr>
							</tfoot>
						</table>
					</Card>
				</>
			)}
		</>
	);
};
